import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import type { PaginatedUsers } from '../types';
import Pagination from '../components/Pagination';

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="var(--tx3)">
    <path fillRule="evenodd" d="M6.5 1a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM0 6.5a6.5 6.5 0 1111.596 4.03l3.187 3.187a.75.75 0 11-1.06 1.06L10.536 11.59A6.5 6.5 0 010 6.5z" />
  </svg>
);

export default function UserList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [data, setData] = useState<PaginatedUsers | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), per_page: '25' });
    if (search) params.set('search', search);
    if (activeFilter) params.set('is_active', activeFilter);
    try {
      setData(await api<PaginatedUsers>(`/admin/users?${params}`));
    } finally {
      setLoading(false);
    }
  }, [page, search, activeFilter]);

  useEffect(() => { load(); }, [load]);

  const fmt = (dt: string | null) =>
    dt ? new Date(dt).toLocaleDateString('en', { month: 'short', day: 'numeric', year: '2-digit' }) : '—';

  return (
    <div className="page-content">
      <div style={{ marginBottom: 20 }}>
        <div className="overline">Platform</div>
        <h1 className="page-title">Users</h1>
      </div>

      <div className="filter-bar">
        <div className="search-bar" style={{ flex: 1, maxWidth: 360 }}>
          <SearchIcon />
          <input
            placeholder="Search email or name…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
        <select
          className="select-input"
          value={activeFilter}
          onChange={e => { setActiveFilter(e.target.value); setPage(1); }}
        >
          <option value="">All users</option>
          <option value="true">Active only</option>
          <option value="false">Suspended only</option>
        </select>
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading && !data ? (
          <div className="loading-state"><div className="spinner" /></div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Lang</th>
                  <th>Dashboards</th>
                  <th>Tokens</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.length === 0 && (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', color: 'var(--tx3)', padding: 32 }}>
                      No users found
                    </td>
                  </tr>
                )}
                {data?.items.map(u => (
                  <tr key={u.id} className="clickable" onClick={() => navigate(`/users/${u.id}`)}>
                    <td style={{ fontWeight: 500 }}>{u.email}</td>
                    <td style={{ color: 'var(--tx2)' }}>{u.full_name || '—'}</td>
                    <td>
                      {u.settings?.preferred_language
                        ? <span className="tag">{u.settings.preferred_language.toUpperCase()}</span>
                        : <span style={{ color: 'var(--tx4)' }}>—</span>}
                    </td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{u.dashboard_count}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{u.total_tokens_used.toLocaleString()}</td>
                    <td>
                      {(u.settings?.email_alerts_enabled || u.settings?.email_digest_enabled)
                        ? <span className="badge badge-ok">On</span>
                        : <span className="badge badge-neutral">Off</span>}
                    </td>
                    <td>
                      {u.is_active
                        ? <span className="badge badge-ok">Active</span>
                        : <span className="badge badge-alert">Suspended</span>}
                    </td>
                    <td style={{ color: 'var(--tx3)' }}>{fmt(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data && data.pages > 1 && (
              <div style={{ padding: '0 12px' }}>
                <Pagination page={page} pages={data.pages} total={data.total} perPage={25} onPage={p => { setPage(p); }} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
